// ============================================================================
// GrilleSalarialePage — grille salariale CE / admin (L06, GC-B, F-656..660)
// ============================================================================
// SHELL de la page (GC-B) : PageHeader (annee), tabs URL-reflected (?tab= +
// alias directeurs->cadres, P-CE5), YearPicker (?year=), bandeaux ?ok=/?error=,
// badge rouge submittedCount sur l'onglet Approbations, boutons export (.xlsx +
// PDF, download direct fetch Bearer + blob, STOP-Q3). Le tab GRILLE = complet
// (SalaryMasterGrid + useGrilleCe + buildEmployeeLines). Les tabs approbations/
// graphiques/cadres = placeholders EmptyState « a venir » (GC-D/GC-E) — les liens
// edit/reel sont MASQUES tant que GC-C/GC-E n'ont pas livre leurs routes (anti
// liens morts, lecon POC-DEAD-LINK).
//
// Portage du PoC (dashboard)/grille-salariale/page.tsx (SSR searchParams) au
// standard maison (SPA + useSearchParams). Gabarit : PlanificationPage (SL-A :
// garde client Forbidden, bandeaux, skeleton/QueryError) + MonEquipePage (export
// PDF fetch Bearer + blob, SL-E). Garde M-670 (P-CE14) : la garde REELLE est le
// 403 SERVEUR (admin SEUL ; director/manager/employee refuses). Le SPA ne DECIDE
// pas — il redirige le 403 vers /error/403, EN PLUS de la garde serveur (GA-05).

import { useState } from 'react';
import { Link, Navigate, useSearchParams } from 'react-router-dom';
import { PageHeader, EmptyState, SkeletonCard, QueryError, GS_COMPONENTS } from '@gs/ui';
import { Table2, Download, FileText, Hourglass, Pencil } from 'lucide-react';
import { ApiForbiddenError, getRegisteredAccessToken } from '@gs/api-client';
import { useAuth } from '@gs/auth';
import { useTranslation } from '../../i18n';
import { useGrilleCe } from '../../hooks/useGrilleCe';
import { adaptGridClasses } from '../../hooks/usePlanification';
import { buildEmployeeLines } from '../../lib/salary/positions';
import { toLibPast, toLibHistory } from '../../lib/salary/dto-adapters';
import { SalaryMasterGrid } from '../../components/salary/SalaryMasterGrid';
import { YearPicker } from '../../components/salary/YearPicker';

const TAB_KEYS = ['grille', 'approbations', 'graphiques', 'cadres'] as const;
type GrilleTab = (typeof TAB_KEYS)[number];

/** Alias retrocompatible `directeurs`->`cadres` (P-CE5), fallback `grille`. */
function resolveTab(raw: string | null): GrilleTab {
  const aliased = raw === 'directeurs' ? 'cadres' : raw;
  return (TAB_KEYS as readonly string[]).includes(aliased ?? '') ? (aliased as GrilleTab) : 'grille';
}

const BASE_URL: string = import.meta.env.VITE_API_URL ?? '';

export default function GrilleSalarialePage() {
  const { t } = useTranslation();
  const { user } = useAuth();
  const [searchParams] = useSearchParams();

  const tab = resolveTab(searchParams.get('tab'));
  const yearParam = searchParams.get('year');
  const okCode = searchParams.get('ok'); // approved / rejected (retour de decision GC-D)
  const errorCode = searchParams.get('error');

  const [exporting, setExporting] = useState(false);
  const [exportError, setExportError] = useState<string | null>(null);

  const q = useGrilleCe(yearParam ? Number(yearParam) : null, !!user);

  // Garde CLIENT M-670 : le 403 serveur (non-admin) -> ecran d'erreur standard. La
  // redirection ne REMPLACE pas la garde serveur, elle la double (GA-05).
  if (q.isError && q.error instanceof ApiForbiddenError) {
    return <Navigate to="/error/403" replace />;
  }

  const data = q.data;
  const selectedYear = data?.year ?? new Date().getFullYear();
  const currentYear = data?.currentYear ?? selectedYear;
  // Annees selectionnables : figees ∪ courante (le serveur a deja resolu l'annee).
  const selectable = data
    ? [...new Set([...data.gridYears, data.currentYear])].sort((a, b) => b - a)
    : [];
  const submittedCount = data?.submittedCount ?? 0;
  const hasGrid = (data?.classes.length ?? 0) > 0;

  // YearPicker : masque sur l'onglet approbations (annee courante forcee, P-CE17) ;
  // visible ailleurs ssi >1 annee selectionnable.
  const yearPicker =
    tab !== 'approbations' && selectable.length > 1 ? (
      <YearPicker years={selectable} value={selectedYear} />
    ) : null;

  // Liens d'onglets en preservant l'annee (le YearPicker preserve de son cote le tab).
  const tabHref = (value: GrilleTab) => {
    const p = new URLSearchParams();
    if (value !== 'grille') p.set('tab', value);
    if (selectedYear !== currentYear) p.set('year', String(selectedYear));
    const qs = p.toString();
    return qs ? `/grille-salariale?${qs}` : '/grille-salariale';
  };

  // Export .xlsx (MASTER, pas d'annee — quirk P-CE17) et PDF (year-aware) : download
  // direct (STOP-Q3) — fetch Bearer + blob (SL-E), jamais un <a href> nu (le Bearer
  // GS ne voyage pas dans une navigation). Chemin RELATIF uniquement.
  async function downloadExport(kind: 'xlsx' | 'pdf') {
    const getToken = getRegisteredAccessToken();
    if (!getToken) {
      setExportError(kind === 'xlsx' ? 'exportXlsx' : 'exportPdf');
      return;
    }
    setExporting(true);
    setExportError(null);
    try {
      const token = await getToken();
      const path =
        kind === 'xlsx'
          ? '/api/grille-salariale/export'
          : `/api/grille-salariale/export-pdf${selectedYear !== currentYear ? `?year=${selectedYear}` : ''}`;
      const res = await fetch(`${BASE_URL}${path}`, { headers: { Authorization: `Bearer ${token}` } });
      if (!res.ok) throw new Error(String(res.status));
      const blob = await res.blob();
      const objectUrl = URL.createObjectURL(blob);
      const anchor = document.createElement('a');
      anchor.href = objectUrl;
      anchor.download =
        kind === 'xlsx'
          ? 'grille-salariale-2026.xlsx'
          : `grille-salariale-generale-${selectedYear}.pdf`;
      document.body.appendChild(anchor);
      anchor.click();
      anchor.remove();
      URL.revokeObjectURL(objectUrl);
    } catch {
      setExportError(kind === 'xlsx' ? 'exportXlsx' : 'exportPdf');
    } finally {
      setExporting(false);
    }
  }

  // Contenu du tab GRILLE : lignes employes GLOBALES pivotees client. `currentYear`
  // (annee reelle du bundle) est le PIVOT (passe = annees < currentYear) — VERBATIM
  // PoC page.tsx:158-164 (NON selectedYear) ; SalaryMasterGrid affiche `selectedYear`.
  const grilleContent = (() => {
    if (!data) return null;
    if (!hasGrid) {
      // EmptyState SANS lien editeur (GC-C non livre — anti liens morts).
      return (
        <EmptyState
          icon={<Table2 size={48} aria-hidden="true" />}
          title={t('gest:grilleCe.empty.title')}
          description={t('gest:grilleCe.empty.description')}
        />
      );
    }
    const linesByClass = buildEmployeeLines({
      members: data.members,
      pastPositions: toLibPast(data.pastByEmail),
      echelonHistory: toLibHistory(data.historyByEmail),
      scenarioChanges: [],
      currentYear: data.currentYear,
    });
    return (
      <SalaryMasterGrid
        classes={adaptGridClasses(data.classes)}
        linesByClass={linesByClass}
        year={selectedYear}
        shadeInactive={data.shadeInactive}
        headerActions={yearPicker}
      />
    );
  })();

  // Placeholder « a venir » commun aux tabs GC-D/GC-E (le YearPicker reste visible
  // dans l'en-tete de section pour graphiques/cadres — coherence P-CE17).
  const comingSoon = (
    <EmptyState
      icon={<Hourglass size={48} aria-hidden="true" />}
      title={t('gest:grilleCe.comingSoon.title')}
      description={t('gest:grilleCe.comingSoon.description')}
    />
  );

  return (
    <div className="space-y-5">
      <PageHeader
        title={t('gest:grilleCe.title', { year: selectedYear })}
        description={t('gest:grilleCe.pageDescription')}
        onRefresh={() => void q.refetch()}
        refreshLoading={q.isFetching}
        actions={
          <div className="flex flex-wrap items-center gap-2">
            {/* Exports : uniquement si une grille existe (rien a exporter sinon). */}
            {hasGrid ? (
              <>
                <button
                  type="button"
                  disabled={exporting}
                  onClick={() => void downloadExport('xlsx')}
                  className={`${GS_COMPONENTS.btnSecondary} inline-flex touch-manipulation items-center gap-2 disabled:opacity-50`}
                >
                  <Download size={16} aria-hidden="true" />
                  {t('gest:grilleCe.exportXlsx')}
                </button>
                <button
                  type="button"
                  disabled={exporting}
                  onClick={() => void downloadExport('pdf')}
                  className={`${GS_COMPONENTS.btnSecondary} inline-flex touch-manipulation items-center gap-2 disabled:opacity-50`}
                >
                  <FileText size={16} aria-hidden="true" />
                  {t('gest:grilleCe.exportPdf')}
                </button>
              </>
            ) : null}
            {/* Editer (GC-C, demasque) : toujours visible (page ADMIN — construire la
                grille meme vide passe par l'editeur). Route gardee M-670 serveur. */}
            <Link
              to="/grille-salariale/edit"
              className={`${GS_COMPONENTS.btnAction} inline-flex touch-manipulation items-center gap-2`}
            >
              <Pencil size={16} aria-hidden="true" />
              {t('gest:grilleCe.editLink')}
            </Link>
          </div>
        }
      />

      {/* Tabs URL-reflected (P-CE5) — badge rouge submittedCount sur Approbations
          (info REELLE meme si l'onglet est un placeholder GC-D). Motif ProfileTabNav. */}
      <nav
        aria-label={t('gest:grilleCe.tabsLabel')}
        className="-mb-px flex gap-1 overflow-x-auto border-b border-slate-200 dark:border-slate-700"
      >
        {TAB_KEYS.map((key) => {
          const isActive = key === tab;
          return (
            <Link
              key={key}
              to={tabHref(key)}
              aria-current={isActive ? 'page' : undefined}
              className={`inline-flex touch-manipulation items-center whitespace-nowrap border-b-2 px-3 py-2 text-field font-medium transition-colors ${
                isActive
                  ? 'border-brand-500 text-brand-600 dark:text-brand-400'
                  : 'border-transparent text-slate-500 hover:border-slate-300 hover:text-slate-700 dark:text-slate-400 dark:hover:text-slate-200'
              }`}
            >
              {t(`gest:grilleCe.tabs.${key}`)}
              {key === 'approbations' && submittedCount > 0 ? (
                <span className="ml-1.5 inline-flex min-w-4 items-center justify-center rounded-full bg-red-500 px-1 text-chip font-semibold tabular-nums text-white">
                  {submittedCount}
                </span>
              ) : null}
            </Link>
          );
        })}
      </nav>

      {/* Bandeau ?ok= (retour de decision GC-D, deep-linkable). */}
      {okCode ? (
        <div
          role="status"
          className="rounded-xl border-l-4 border-green-500 bg-green-50 p-4 text-field text-green-700 dark:bg-green-950 dark:text-green-300"
        >
          {t(`gest:grilleCe.ok.${okCode}`, { defaultValue: t('gest:grilleCe.ok.approved') })}
        </div>
      ) : null}

      {/* Bandeau ?error= + erreur d'export locale (GA-21 : jamais un code brut). */}
      {errorCode || exportError ? (
        <div
          role="alert"
          className="rounded-xl border-l-4 border-red-500 bg-red-50 p-4 text-field text-red-700 dark:bg-red-950 dark:text-red-300"
        >
          {t(`gest:grilleCe.errors.${errorCode ?? exportError}`, {
            defaultValue: t('gest:grilleCe.errors.server'),
          })}
        </div>
      ) : null}

      {q.isLoading ? (
        <div className="grid grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-4">
          <SkeletonCard />
          <SkeletonCard />
          <SkeletonCard />
          <SkeletonCard />
        </div>
      ) : q.isError ? (
        <QueryError message={t('gest:grilleCe.loadError')} onRetry={() => void q.refetch()} />
      ) : data ? (
        tab === 'grille' ? (
          grilleContent
        ) : (
          <section className={`${GS_COMPONENTS.card} p-5`}>
            <div className="mb-4 flex flex-wrap items-start justify-between gap-3">
              <div className="flex flex-col gap-0.5">
                <span className="text-chip font-medium uppercase tracking-wide text-slate-400">
                  {t(`gest:grilleCe.sections.${tab}.eyebrow`)}
                </span>
                <h2 className="text-body font-semibold text-slate-800 dark:text-slate-100">
                  {t(`gest:grilleCe.sections.${tab}.title`)}
                </h2>
              </div>
              {yearPicker}
            </div>
            {comingSoon}
          </section>
        )
      ) : null}
    </div>
  );
}
